import { regexPhone, spError, spErrorTags } from '@eventr/common'
import {
    ColoursObj,
    FormInputCountrySelectorV2,
    FormInputText,
    MainButton,
    Typography,
    useIpInfo,
} from '@eventr/common-storybook'
import { zodResolver } from '@hookform/resolvers/zod'
import { Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { requestsEventrSharedNoAuth } from '../../../data/api'
import { EventrGatewayAPI } from '../../../data/gateway.api'
import { UserAPI } from '../../../data/User'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { getNotParsedUrlParam } from '../../../utils'
import { logEvent } from '../../firebase/firebase'
import { useGlobalRouter } from '../../globalRouter/useGlobalRouter'
import { useGuestStore } from '../../guest/guest.store'
import { NavigationView } from '../../layout/NavigationView'
import { SocialLogins } from '../components/socialLogins'
import { useRegistrationContext, useRegStore } from '../registrationProvider'

const validationSchema = z.object({
    email: z.string().email({ message: 'Your email is invalid.' }),
    firstName: z.string().min(1, { message: 'Your first name is required.' }),
    lastName: z.string().min(1, { message: 'Your last name is required.' }),
    country: z.string().min(1, { message: 'Your country is required.' }),
    phoneNumber: z
        .string()
        .regex(regexPhone, {
            message: 'Your phone number is invalid.',
        })
        .optional()
        .or(z.literal('')),
})

type ValidationSchema = z.infer<typeof validationSchema>

/* eslint-disable react-hooks/exhaustive-deps */
export function AddDetails() {
    const { confirmInvite } = useRegistrationContext()
    const { pushPath } = useGlobalRouter()
    const { inviteUserData, setInviteData } = useRegStore((s) => {
        return {
            inviteUserData: s.inviteUserData,
            setInviteData: s.setInviteData,
        }
    })
    const wpUser = useBoundStore((s) => s.wpUser)
    const ipCountryCode = useIpInfo(
        (s: { ipInfo: { countryCode: any } }) => s.ipInfo.countryCode
    )

    // Pulling token and destination from URL parameters:
    const emailToken: string = getNotParsedUrlParam('token')
    const org: string = getNotParsedUrlParam('org')
    const email: string = getNotParsedUrlParam('email')

    const [confirmResp, setConfirmResp] = useState<any>(undefined)

    const methods = useForm<ValidationSchema>({
        resolver: zodResolver(validationSchema),
    })
    const { handleSubmit, control, setValue } = methods

    useEffect(() => {
        if (inviteUserData) {
            setValue('firstName', inviteUserData.firstName ?? '')
            setValue('lastName', inviteUserData.lastName ?? '')
            setValue('phoneNumber', inviteUserData.phoneNumber)
            setValue('country', inviteUserData.countryCode ?? ipCountryCode)
            setValue('email', inviteUserData.email ?? '')
        }
    }, [inviteUserData])

    const onSubmit: SubmitHandler<ValidationSchema> = (data) => {
        loadingSignal.value = true
        try {
            EventrGatewayAPI.profile.exists(data.email).then((res) => {
                if (res.data) {
                    defaultAddSnack(
                        'Account with this email already exist',
                        'error'
                    )
                    return
                }
                setInviteData({
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phoneNumber: data.phoneNumber,
                    countryCode: data.country,
                })
                logEvent('sign_up_set_password', {
                    guestCopy: useGuestStore.getState().cloneOnSignUp,
                })
                if (inviteUserData && inviteUserData.isEIDP) {
                    pushPath(`/authentication/termsAndPolicies`, {
                        animation: 'right',
                    })
                    return
                }
                pushPath(`/authentication/setPassword`, {
                    animation: 'right',
                })
            })
        } catch {
            defaultAddSnack(
                'Something went wrong, please try again later.',
                'error'
            )
        } finally {
            loadingSignal.value = false
        }
    }
    // This effect is for the invited user flow.
    //It will determine if the user is the same as the invite and set the state accordingly.
    useEffect(() => {
        if (!emailToken) {
            return
        }

        if (confirmResp) {
            return
        }
        // set the base url for the wordpres api
        useBoundStore.setState({ selectedOrg: { name: org } })
        const confirm = async () => {
            if (confirmInvite) {
                return await confirmInvite(org, emailToken)
            } else {
                throw new Error('confirmInvite is not defined')
            }
        }
        const requestToken = async () => {
            return await requestsEventrSharedNoAuth.post(
                'auth/tokenFromEmail',
                {
                    token: emailToken,
                }
            )
        }

        if (wpUser?.email && inviteUserData?.ignoreUserMismatch !== true) {
            loadingSignal.value = true
            requestToken()
                .then((response) => {
                    if (response.status === 'success') {
                        const email = response.data.user_email
                        logEvent('sign_up_invited_flow_started', {
                            email: email,
                        })

                        if (wpUser.email !== email) {
                            setInviteData({
                                email: email.toLowerCase(),
                                wp_id: response.data.wp_id,
                                org: org,
                                flow_type: 'invite',
                                notSameUser: true,
                            })
                            loadingSignal.value = false
                            pushPath('/authentication/notSameUser')
                        } else {
                            loadingSignal.value = true
                            confirm().then((res) => {
                                if (res.status === 'error') {
                                    spError(
                                        'Error confirming invite',
                                        'confirmInvite',
                                        res.message
                                    )
                                    if (
                                        res.message === 'PS - Org already added'
                                    ) {
                                        defaultAddSnack(
                                            i18n.t('snack.link-been-used'),
                                            'error'
                                        )
                                    } else {
                                        defaultAddSnack(
                                            res.message ??
                                                'An unexpected error occurred',
                                            'error'
                                        )
                                    }
                                    pushPath('/authentication/signin')
                                    loadingSignal.value = false
                                    return
                                }
                                setConfirmResp(res)
                            })
                        }
                    }
                })
                .catch((err) => {
                    spErrorTags('Error confirming invite', ['REGISTER'], err)
                    defaultAddSnack(err.message, 'error')
                    loadingSignal.value = false
                    pushPath('/authentication/signin')
                })
        } else {
            loadingSignal.value = true

            confirm().then((res) => {
                if (res.status === 'error') {
                    spError(
                        'Error confirming invite',
                        'confirmInvite',
                        res.message
                    )
                    if (res.message === 'PS - Org already added') {
                        defaultAddSnack(i18n.t('snack.link-been-used'), 'error')
                    } else {
                        defaultAddSnack(res.message ?? 'Unkown Error', 'error')
                    }
                    loadingSignal.value = false
                    pushPath('/authentication/signin')
                    return
                }
                setConfirmResp(res)
            })
        }
    }, [emailToken, inviteUserData, wpUser?.email, confirmResp, org])

    useEffect(() => {
        if (!confirmResp || org === '') {
            return
        }
        loadingSignal.value = false
        if (confirmResp.status === 'success') {
            if (confirmResp.data.status === 'new') {
                setInviteData({
                    email: confirmResp.data.tokenFromEmail.user_email.toLowerCase(),
                    wp_id: confirmResp.data.tokenFromEmail.wp_id,
                    org: org,
                    flow_type: 'invite',
                    notSameUser: true,
                })
                return
            }
            if (confirmResp.data.status === 'exist') {
                if (confirmResp.data.email)
                    setInviteData({
                        email: confirmResp.data.email.toLowerCase(),
                        org: org,
                    })
                pushPath('/authentication/joined')
            }
        } else {
            spError(
                'Error confirming invite',
                'confirmInvite',
                confirmResp.message
            )
            defaultAddSnack(i18n.t('snack.failed-confirm-invite'), 'error')
        }
    }, [confirmResp, org])
    //this effect os for user creation flow without invite. It will pull the email from the URL and set it in the state.
    useEffect(() => {
        if (!email) {
            return
        }
        logEvent('sign_up_add_details', {
            email: email,
            guestCopy: useGuestStore.getState().cloneOnSignUp,
        })
        const checkUser = async () => {
            if (
                inviteUserData?.ignoreUserMismatch !== true &&
                wpUser &&
                wpUser.email.toLowerCase() !==
                    decodeURIComponent(email).toLowerCase()
            ) {
                pushPath('/authentication/notSameUser')
                return
            }

            const userExist = await UserAPI.userExists(email)
            if (userExist) {
                defaultAddSnack(i18n.t('snack.already-have-account'), 'error')
                pushPath('/authentication/signin')
                return
            }
            setInviteData({
                email: decodeURIComponent(email).toLowerCase(),
                flow_type: 'create',
            })
        }
        checkUser()
    }, [email])

    function backButtonAction() {
        window.replacePath('/')
    }

    const onGoogleClick = async () => {
        const { googleResult, resp } = await useBoundStore
            .getState()
            .googleOneTapSignIn()
        if (googleResult) {
            if (resp.data) {
                setInviteData({
                    firstName: googleResult.profile.givenName ?? undefined,
                    lastName: googleResult.profile.familyName ?? undefined,
                    isEIDP: true,
                    authTokens: {
                        expiration: Date.now() + resp.data.expires_in * 1000,
                        IdToken: resp.data.access_token,
                        refreshToken: resp.data.refresh_token,
                    },
                })
            }
        }
    }
    const onEntraClick = async () => {
        const { googleResult, resp } = await useBoundStore
            .getState()
            .googleOneTapSignIn()
        if (googleResult) {
            if (resp.data) {
                setInviteData({
                    firstName: googleResult.profile.givenName ?? undefined,
                    lastName: googleResult.profile.familyName ?? undefined,
                    isEIDP: true,
                    authTokens: {
                        expiration: Date.now() + resp.data.expires_in * 1000,
                        IdToken: resp.data.access_token,
                        refreshToken: resp.data.refresh_token,
                    },
                })
            }
        }
    }

    const onAppleClick = async () => {
        const { appleResult, resp } = await useBoundStore
            .getState()
            .appleOneTapSignIn()
        if (appleResult) {
            if (resp.data) {
                setInviteData({
                    firstName: appleResult.profile.givenName ?? undefined,
                    lastName: appleResult.profile.familyName ?? undefined,
                    isEIDP: true,
                    authTokens: {
                        expiration: Date.now() + resp.data.expires_in * 1000,
                        IdToken: resp.data.access_token,
                        refreshToken: resp.data.refresh_token,
                    },
                })
            }
        }
    }

    const firstNameTextField = () => {
        if (inviteUserData?.isEIDP && inviteUserData.firstName) {
            return (
                <Stack
                    padding="8px"
                    bgcolor={ColoursObj.shades.shade4}
                    borderRadius="8px"
                >
                    <Typography variant="bodySubtext">
                        {i18n.t('admin.first-name')}
                    </Typography>
                    <Typography
                        variant="bodyRegular"
                        id="prefilledUserFirstname"
                    >
                        {inviteUserData.firstName}
                    </Typography>
                </Stack>
            )
        }
        return (
            <FormInputText
                id="firstNameField"
                control={control as any}
                name="firstName"
                type="text"
                fieldLabel={i18n.t('admin.first-name')}
                required
            />
        )
    }

    const lastNameTextField = () => {
        if (inviteUserData?.isEIDP && inviteUserData.lastName) {
            return (
                <Stack
                    padding="8px"
                    bgcolor={ColoursObj.shades.shade4}
                    borderRadius="8px"
                >
                    <Typography variant="bodySubtext">
                        {i18n.t('admin.last-name')}
                    </Typography>
                    <Typography
                        variant="bodyRegular"
                        id="prefilledUserLastName"
                    >
                        {inviteUserData.lastName}
                    </Typography>
                </Stack>
            )
        }
        return (
            <FormInputText
                id="lastNameField"
                control={control as any}
                name="lastName"
                type="text"
                fieldLabel={i18n.t('admin.last-name')}
                required
            />
        )
    }

    const phoneNumberTextField = (
        <FormInputText
            id="telephoneField"
            control={control as any}
            name="phoneNumber"
            type="tel"
            fieldLabel={i18n.t('account.profile.phoneNumber')}
        />
    )
    const countryField = (
        <FormInputCountrySelectorV2
            id="countryField"
            name="country"
            fieldLabel={i18n.t('edit-itinerary.fields.country.field-label')}
            placeholder={i18n.t('edit-itinerary.fields.country.placeholder')}
            control={control as any}
        />
    )

    const emailLabel = () => {
        if (inviteUserData?.flow_type === 'inviteLink') {
            return (
                <FormInputText
                    id="emailField"
                    required
                    control={control as any}
                    name="email"
                    type="email"
                    fieldLabel={i18n.t('general.email')}
                />
            )
        }

        return (
            <Stack
                padding="8px"
                bgcolor={ColoursObj.shades.shade4}
                borderRadius="8px"
            >
                <Typography variant="bodySubtext">
                    {i18n.t('general.email')}
                </Typography>
                <Typography variant="bodyRegular" id="prefilledUserEmail">
                    {inviteUserData?.email}
                </Typography>
            </Stack>
        )
    }
    const socialLinks = (
        <SocialLogins
            toptext={i18n.t('authentication.sign-in.or-sign-in-with')}
            onAppleClick={() => {
                onAppleClick()
            }}
            onGoogleClick={() => {
                onGoogleClick()
            }}
            onEntraClick={() => {
                useBoundStore.getState().signInWithProvider('entra')
            }}
        />
    )

    const body = (
        <Stack padding="16px" gap="8px">
            <FormProvider {...methods}>
                {emailLabel()}
                {firstNameTextField()}

                {lastNameTextField()}

                {phoneNumberTextField}

                {countryField}
                {emailToken && socialLinks}
            </FormProvider>
        </Stack>
    )

    const footer = (
        <Stack padding="16px">
            <MainButton
                id="continueButton"
                text={i18n.t('general.continue')}
                onClick={() => handleSubmit(onSubmit)()}
            />
        </Stack>
    )

    return (
        <NavigationView
            navigationBarTitle={i18n.t(
                'authentication.add-details.add-your-dets'
            )}
            navigationBarSubtitle={i18n.t(
                'authentication.add-details.complete-account'
            )}
            navigationBarDisplayMode="large"
            usePageTransition
            backButtonAction={backButtonAction}
            isRoot
            footer={footer}
        >
            {body}
        </NavigationView>
    )
}
